"use client";

import { forwardRef } from "react";
import { cn } from "./cn";

export type ButtonVariant =
  | "primary"
  | "primarySplit"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger"
  | "menuItem"
  | "dashedAdd"
  | "filterPill"
  | "chipPill";

export type ButtonSize = "sm" | "md" | "lg";

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  active?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
};

const SIZE: Record<ButtonSize, string> = {
  sm: "h-8 px-2.5 text-[12.5px]",
  md: "h-9 px-3.5 text-[13px]",
  lg: "h-10 px-4 text-[13.5px]",
};

const GLYPH = "w-3.5 h-3.5 shrink-0 [&_svg]:w-full [&_svg]:h-full";

const base =
  "inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg transition disabled:cursor-not-allowed select-none";

function variantCls(variant: ButtonVariant, active: boolean): string {
  switch (variant) {
    case "primary":
      return "bg-brand hover:bg-brand-hover text-white disabled:bg-[#98A2B3]";
    case "primarySplit":
      return "bg-brand hover:bg-brand-hover text-white rounded-l-none border-l border-white/25";
    case "secondary":
      return "border border-[#101828] text-[#101828] bg-white hover:bg-[#F9FAFB] disabled:border-[#E4E7EC] disabled:text-[#98A2B3]";
    case "outline":
      return "border border-[#D0D5DD] text-[#344054] bg-white shadow-sm hover:bg-[#F9FAFB] disabled:border-[#E4E7EC] disabled:text-[#98A2B3]";
    case "ghost":
      return "text-[#6B7280] hover:text-[#101828] hover:bg-[#F9FAFB]";
    case "danger":
      return "text-[#B42318] hover:bg-[#FEF3F2]";
    case "menuItem":
      return cn(
        "w-full !justify-start rounded-lg px-2.5 !h-auto py-2 gap-2.5 text-[13px] font-medium",
        "text-[#101828] hover:bg-[#F9FAFB]",
      );
    case "dashedAdd":
      return cn(
        "w-full border border-dashed border-[#D0D5DD] bg-white text-[#475467]",
        "hover:text-brand hover:border-brand/50",
      );
    case "filterPill":
      return active
        ? "rounded-lg border border-brand bg-brand-subtle text-brand-cta"
        : "rounded-lg border border-dashed border-[#D0D5DD] bg-white text-[#475467] hover:border-[#D0D5DD]";
    case "chipPill":
      return active
        ? "rounded-full border border-brand bg-brand-subtle text-brand-cta"
        : "rounded-full border border-[#E4E7EC] bg-white text-[#475467] hover:bg-[#F9FAFB]";
  }
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = "primary",
    size = "md",
    active = false,
    leftIcon,
    rightIcon,
    fullWidth,
    className,
    type = "button",
    children,
    ...rest
  },
  ref,
) {
  const sizeCls = variant === "menuItem" ? "" : SIZE[size];
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        base,
        sizeCls,
        variantCls(variant, active),
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {leftIcon && <span className={GLYPH}>{leftIcon}</span>}
      {children}
      {rightIcon && <span className={GLYPH}>{rightIcon}</span>}
    </button>
  );
});


export type IconButtonSize = "sm" | "md" | "lg";
export type IconButtonVariant = "bare" | "bordered" | "subtle";
export type IconButtonTone = "default" | "danger";

const ICON_SIZE: Record<IconButtonSize, string> = {
  sm: "w-6 h-6 rounded-md",
  md: "w-8 h-8 rounded-lg",
  lg: "w-9 h-9 rounded-lg",
};

const ICON_GLYPH: Record<IconButtonSize, string> = {
  sm: "w-3.5 h-3.5 [&_svg]:w-full [&_svg]:h-full",
  md: "w-4 h-4 [&_svg]:w-full [&_svg]:h-full",
  lg: "w-4 h-4 [&_svg]:w-full [&_svg]:h-full",
};

type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  icon: React.ReactNode;
  label: string;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  tone?: IconButtonTone;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      size = "md",
      variant = "bare",
      tone = "default",
      className,
      type = "button",
      ...rest
    },
    ref,
  ) {
    const variantCls =
      variant === "bordered"
        ? "border border-[#E4E7EC] bg-white hover:bg-[#F9FAFB]"
        : variant === "subtle"
          ? "bg-[#F2F4F7] hover:bg-[#EAECF0]"
          : "hover:bg-[#F2F4F7]";

    const toneCls =
      tone === "danger"
        ? "text-[#B42318] hover:text-[#7A1210] hover:bg-[#FEF3F2]"
        : "text-[#707684] hover:text-[#101828]";

    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={label}
        className={cn(
          "inline-flex items-center justify-center transition",
          ICON_SIZE[size],
          variantCls,
          toneCls,
          className,
        )}
        {...rest}
      >
        <span className={ICON_GLYPH[size]}>{icon}</span>
      </button>
    );
  },
);


export const buttonRecipe = (opts: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  active?: boolean;
  fullWidth?: boolean;
} = {}) =>
  cn(
    base,
    (opts.variant ?? "primary") === "menuItem" ? "" : SIZE[opts.size ?? "md"],
    variantCls(opts.variant ?? "primary", opts.active ?? false),
    opts.fullWidth && "w-full",
  );
